import axios from "axios";
import { cpf } from "cpf-cnpj-validator";
import * as EmailValidator from "email-validator";
import { useFormik } from "formik";
import pagarme from "pagarme/browser";
import React, { useEffect, useRef, useState } from "react";
import { FEATURE_FLAGS } from "../../../app/constants/FeatureFlag";
import useFeatureFlag from "../../../app/hooks/useFeatureFlag";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import * as Sentry from "@sentry/react";

import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { config } from "../../../config/env";

import { useRecaptchaV2, useRecaptchaV3 } from "../../hooks/useRecaptcha";
import { validateCpf, validateEmail } from "./api";
import { getDocumentValidationErrorMessage } from "./documentValidation";
import { trackSignUpAccountCreationSubmit } from "app/services/tracking/payments/cadastroEvents";
import {
  trackCheckoutCardTokenizationError,
  trackCheckoutCardTokenizationSuccess,
  trackCheckoutPaymentFailed,
  trackCheckoutPixPaymentClicked,
  trackCheckoutPixPaymentFailed,
  trackCheckoutSignUpAttempt,
  trackCheckoutTryAgainClicked,
  trackCheckoutAccessPlatformClicked,
  trackSignUpAccountCreated,
  trackSignUpAccountCreationFailed,
} from "app/services/tracking/payments/checkoutStepsEvents";
import Step1 from "./Steps/Step1";
import Step2 from "./Steps/Step2";
import CadastroPlanGate from "./components/CadastroPlanGate";
import { OabPartnershipModal } from "./components/OabPartnershipModal";
import { isCaaPrSpecialCoupon } from "./caaPartnerOabCoupons";
import traceId from "../../../redux/traceId";
import { ModalFailedPayment } from "../../components/ModalFailedPayment";
import { tracking } from "../../services/tracking";
import { ModalProcessingPayment } from "../../components/ModalProcessingPayment";
import { ModalPaymentSuccess } from "../../components/ModalPaymentSuccess";
import { ModalCheckoutPixPayment } from "../../components/ModalCheckoutPixPayment";
import {
  CHECKOUT_PROVIDER,
  resolveCheckoutProvider,
  resolveEffectiveJussubEnabled,
  useCheckoutProviderMode
} from "../CadastroV2/useCheckoutProviderMode";
import { buildJussubCheckoutPayload } from "../CadastroV2/useCheckoutFlow";
import { FIRST_ACCESS_ROUTES } from "../FirstAccess/routes";
import {
  buildCheckoutRegisterPayload,
  buildCheckoutValues,
  getJusbillSubscriptionPath,
} from "../../services/checkout/checkoutMode";
import { buildTrackingUrlParams } from "../../services/checkout/trackingParams";
import { useCheckoutPlans } from "../CadastroV2/useCheckoutPlans";
import { focusFirstInvalidField } from "./focusFirstInvalidField";
import {
  isCouponMatchedInCatalog as isCouponMatchedInCatalogHelper,
  reconcileCatalogPlan,
  resolveCheckoutPlanAndCoupom,
  resolveCheckoutPlanId,
  resolveExplicitPlan,
} from "./plans";

export default function Register(props) {
  const history = useHistory();
  const {
    isFeatureFlagEnable: enableAddCardSentry,
    flagsReady,
  } = useFeatureFlag(FEATURE_FLAGS.KILL_SWITCH.ENABLE_ADD_CARD_SENTRY);

  const {
    isJussubEnabled: rawIsJussubEnabled,
    isResolved: isJussubResolved,
  } = useCheckoutProviderMode();
  
  // ETAPA 1 :: JUSSUB habilitado apenas no /cadastro
  // const rawIsJussubEnabled = false;
  // const isJussubResolved = true;
  // const isJussubEnabled = false;

  const coupom = useSelector(state => state.app.coupom);
  // Enquanto o cupom valida, coupom.is_jussub ainda não existe e isJussubEnabled cai pra
  // flag crua — sem bloquear o submit aqui, dava pra finalizar com o provider errado
  // nessa janela (mesmo motivo do bug que motivou a cascata).
  const isLoadingCoupom = useSelector(state => state.app.is_loading_coupom);
  // Última resolução definitiva de coupom.is_jussub (true/false/null). O próprio
  // LOAD_COUPOM zera coupom pra {} enquanto valida — sem preservar essa resolução, o
  // provider efetivo oscilava de volta pro valor cru da flag só durante o loading,
  // fazendo o useCheckoutPlans trocar de catálogo (e possivelmente resetar o plano
  // selecionado) por causa de um estado puramente transitório, mesmo com o submit já
  // bloqueado nessa janela. Só atualiza numa resolução real, nunca no reset de loading.
  const lastCoupomIsJussubRef = useRef(null);
  useEffect(() => {
    if (typeof coupom?.is_jussub === "boolean" || coupom?.is_jussub === null) {
      lastCoupomIsJussubRef.current = coupom.is_jussub;
    }
  }, [coupom]);
  const stableCoupom = isLoadingCoupom
    ? { is_jussub: lastCoupomIsJussubRef.current }
    : coupom;
  // O resultado da validação do cupom (cascata Jussub→Jusbill, ver appRedux.loadCoupom)
  // vale mais que o valor cru da flag pra decidir o provider do checkout inteiro.
  const isJussubEnabled = resolveEffectiveJussubEnabled({
    isJussubEnabled: rawIsJussubEnabled,
    coupom: stableCoupom,
  });
  const [disableButton, setDisableButton] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [failedReason, setFailedReason] = useState({
    message: "",
    details: "",
  });

  // código do cupom "aplicado" (link ?redirect ou botão aplicar do Step2),
  // não o valor bruto do input a cada tecla — mesma cadência de disparo do LOAD_COUPOM existente,
  // evitando 1 GET por tecla no catálogo novo (guardrail: blast radius × pods).
  const [coupomCode, setCoupomCode] = useState("");
  const [isOabPartnershipModalOpen, setIsOabPartnershipModalOpen] = useState(
    false
  );
  const [oabPartnershipNotice, setOabPartnershipNotice] = useState(undefined);

  const {
    plans: cadastroPlans,
    isLoading: isPricingCatalogLoading,
    isError: isPricingCatalogBlockingError,
    refetch: refetchPricingCatalog,
    offers: pricingCatalogOffers,
  } = useCheckoutPlans({
    isJussubEnabled,
    isJussubResolved,
    coupomCode,
  });

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState("idle"); // 'idle' | 'success' | 'error'
  const [pixPaymentData, setPixPaymentData] = useState(null);
  const [pixGenerationFailed, setPixGenerationFailed] = useState(false);

  const [step, setStep] = useState(0);
  const [shouldValidateDocument, setShouldValidateDocument] = useState(true);
  const recaptchaRef = React.createRef();
  const recaptchaValue = recaptchaRef?.current?.getValue();
  const subscription_error = useSelector(
    state => state.subscription.subscription_error
  );
  const [showCupomReferenceInputs, setShowCupomReferenceInputs] = useState(
    false
  );

  const dispatch = useDispatch();
  const [uniqueEmail, setUniqueEmail] = useState(false);

  useEffect(() => {
    if (subscription_error) {
      setResult("error");
      setSubmitting(false);
    }
  }, [subscription_error]);

  const location = useLocation();

  // trava o seletor num card só quando ?plan= ou o cupom aplicado (path/digitado) resolve
  // explicitamente pra um plano — não depende do planParamConsumedRef do efeito abaixo, que só
  // controla se a seleção automática pode sobrescrever a escolha do usuário, não a visibilidade.
  const lockedPlan = cadastroPlans.length
    ? resolveExplicitPlan({
        plans: cadastroPlans,
        planParam: new URLSearchParams(location.search).get("plan"),
        coupomCode,
      })
    : undefined;
  const selectablePlans = lockedPlan ? [lockedPlan] : cadastroPlans;

  const [initialValues] = useState({
    createdCustomerId: "",
    name: "",
    email: "",
    confirmation_email: "",
    profession: null,
    coupom: "",
    document_number: "",
    phone: "",
    postal_code: "",
    city: "",
    state: "",
    address: "",
    address_number: "",
    address_complement: "",
    district: "",
    plan: null,
    cardholder_name: "",
    card_number: "",
    card_payment_method: null,
    card_cvv: "",
    card_expiration: "",
    recaptchaToken: "",
    recaptchaVersion: "",
    reference_name: null,
    reference_city: null,
    has_compra_garantida: false,
  });

  // const disabledCreditCard = useSelector(state =>
  //   Object.keys(state.app.coupom).length > 0 && state.app.coupom?.is_valid
  //     ? !state?.app?.coupom?.upfront_credit_card_required
  //     : false
  // );
  const disabledCreditCard = false;
  const verifyCoupon = useSelector(state => {
    if (Object.keys(state.app.coupom).length !== 0) {
      if (state.app.coupom.is_valid) {
        return true;
      }
      return false;
    }
  });
  const defaultTrialDays = useSelector(state => state.app.default_trial_days);

  // predicado do Step2: grid de tiers vs. card único do cupom legado
  // (válido no validate, ausente do catálogo). O checkout não depende mais disso —
  // resolveCheckoutPlanAndCoupom já cai no plan_id de /coupons/validate nesse caso.
  const isCouponMatchedInCatalog = isCouponMatchedInCatalogHelper({
    coupomCode,
    offers: pricingCatalogOffers,
  });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const step1Validation = yup.object().shape({
    name: yup.string().required("O campo nome é obrigatório!"),
    email: yup
      .string()
      .trim()
      .email("O email é inválido!")
      .required("O campo e-mail é obrigatório!")
      .test("unique-email", "Email já cadastrado", async function(email) {
        const { confirmation_email, name } = this.parent;
        if (
          emailRegex.test(email) &&
          confirmation_email === email &&
          name
        ) {
          if (EmailValidator.validate(email)) {
            try {
              await validateEmail(email);
              return true;
            } catch (error) {
              if (error?.response?.status === 409) {
                setUniqueEmail(true);
                return false;
              }
              setUniqueEmail(false);
              throw new Error("Erro ao validar o email");
            }
          }
        }
        setUniqueEmail(false);
        return true;
      }),
    confirmation_email: yup
      .string()
      .trim()
      .required("O campo confirmação é obrigatório!")
      .email("O email é inválido!")
      .test("valid-confirmation-regex", "Formato de email inválido", value => {
        return emailRegex.test(value ?? "");
      })
      .test("emails-match", "Os emails não coincidem!", function(value) {
        const { email } = this.parent;
        return value === email;
      }),
    profession: yup.object().nullable(),
  });

  let step2Object = {
    plan: yup
      .object()
      .nullable()
      .required("Escolha o plano!"),
    ...(!disabledCreditCard
      ? {
          cardholder_name: yup
            .string()
            .required("O nome do titular do cartão é obrigatório."),
          card_number: yup
            .string()
            .required("O número do cartão é obrigatório."),
          card_expiration: yup
            .string()
            .required("O mês/ano de expiração do cartão é obrigatório."),
          card_cvv: yup
            .string()
            .required("O código de segurança (CVV) do cartão é obrigatório."),
        }
      : {}),
    document_number: yup
      .string()
      .required("O campo CPF é obrigatório!")
      .test("isValidDocument", "O CPF é inválido.", value => cpf.isValid(value))
      .test(
        "isUniqueDocument",
        "Já existe um usuário com este CPF.",
        async function(value) {
          if (value?.length < 14) {
            setShouldValidateDocument(true);
          }
          if (value?.length === 14 && shouldValidateDocument) {
            try {
              await validateCpf(value);
              setShouldValidateDocument(false);
              return true;
            } catch (error) {
              setShouldValidateDocument(true);
              const message = getDocumentValidationErrorMessage(
                error?.response?.status
              );
              if (message) {
                return this.createError({ message });
              }
              return false;
            }
          }
          return true;
        }
      ),
    phone: yup
      .string()
      .required("O campo telefone é obrigatório!")
      .matches(
        /^\([1-9]{2}\) (?:[2-8]|9[1-9])[0-9]{3}\-[0-9]{4}$/,
        "Telefone inválido."
      ),
    ...(showCupomReferenceInputs
      ? {
          reference_name: yup
            .string()
            .nullable()
            .required("O responsável pelo cupom é obrigatório"),
          reference_city: yup
            .string()
            .nullable()
            .required("A região responsável pelo cupom é obrigatória"),
        }
      : {}),
    state: yup.object().required("O campo estado é obrigatório!"),
    city: yup.object().required("O campo cidade é obrigatório!"),
    district: yup.string().required("O campo bairro é obrigatório!"),
    postal_code: yup
      .string()
      .required("O campo CEP é obrigatório!")
      .test(
        "is-valid-cep",
        "CEP inválido. Por favor, insira um CEP válido.",
        function(value) {
          if (!value) {
            setValidCep(true);
            return false;
          }
          if (!isCepValid) return false;
          if (value?.length <= 9) return true;

          return true;
        }
      ),
    address: yup.string().required("O campo endereço é obrigatório!"),
    address_number: yup.string().required("O campo número é obrigatório!"),
    address_complement: yup.string(),
  };
  const step2Validation = yup.object().shape(step2Object);

  const schemaArray = [step1Validation, step2Validation];

  const refreshRecaptchaV3Ref = useRef();

  const [enableRecaptchaV2, setEnableRecaptchaV2] = useState(false);
  const [handleRecaptchaV2Callback, captchaV2Params] = useRecaptchaV2();
  const [handleRecaptchaV3Callback, captchaV3Params] = useRecaptchaV3(
    refreshRecaptchaV3Ref
  );

  const updateFormWithRecaptchaToken = (token, version) => {
    formik.setFieldValue("recaptchaVersion", version);
    formik.setFieldValue("recaptchaToken", token);
  };

  useEffect(() => {
    updateFormWithRecaptchaToken(captchaV3Params.v3Token, "3");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaV3Params.v3Token]);

  useEffect(() => {
    updateFormWithRecaptchaToken(captchaV2Params.v2Token, "2");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaV2Params.v2Token]);

  useEffect(() => {
    captchaV3Params.v3Retrieving
      ? setDisableButton(true)
      : setDisableButton(false);
  }, [captchaV3Params.v3Retrieving]);

  useEffect(() => {
    enableRecaptchaV2 && captchaV2Params.v2Token
      ? setDisableButton(false)
      : setDisableButton(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaV2Params.v2Token]);

  const [isCepValid, setValidCep] = useState(true);

  const searchAddressByAddressCode = async code => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CEP_API_BASE_URL}/${code}/json/`,
        { timeout: 5000 }
      );
      const data = await response.data;
      if (data.erro === "true") {
        setValidCep(false);
        return {
          success: false,
          error: "Não foi possível localizar o CEP",
        };
      } else {
        setValidCep(true);
      }

      return {
        success: true,
        data: {
          address: data.logradouro || "",
          district: data.bairro || "",
          city: data.localidade || "",
          state: data.uf || "",
        },
      };
    } catch (error) {
      if (error.request.timeout === 5000 || error.request.status === 502) {
        setValidCep(true);
        toast.error(
          "Estamos com problemas para localizar o CEP, por favor, insira manualmente."
        );
        return {
          success: true,
        };
      }
      setValidCep(false);
      console.error("Não foi possível localizar o CEP:", error);
      return {
        success: false,
        error: "Não foi possível localizar o CEP",
      };
    }
  };

  const handleSubmitForm = async () => {
    const validation = await formik.validateForm();
    formik.setTouched(validation);

    if (Object.keys(validation).length === 0) {
      if (coupom?.is_partner_coupon) {
        try {
          await axios.post(
            `${config.jusfyBackend.apiUrl}/checkout-validation`,
            {
              document: formik.values.document_number?.replace(/\D/g, ""),
              coupon: formik.values.coupom,
            },
            { headers: { "x-trace-id": traceId }, timeout: 10000 }
          );
        } catch (validationError) {
          setDisableButton(false);
          if (validationError?.response) {
            toast.error(validationError.response.data.message);
          } else {
            toast.error("Não foi possível validar o cupom da parceria. Tente novamente.");
          }
          return;
        }
      }

      setSubmitting(true);
      setResult("idle");
      formik.handleSubmit();
      return;
    }

    const firstErrorKey = Object.keys(formik.errors)[0];
    const firstErrorMessage = formik.errors[firstErrorKey];
    focusFirstInvalidField(validation);
    const currentErrors = Object.entries(validation).map(([field, error]) => ({
      field,
      error,
      step: step,
      timestamp: new Date().toISOString(),
    }));
    setFormErrors(prev => [...prev, ...currentErrors]);
    setFormErrors(prev => [...prev, currentErrors]);
    trackSignUpAccountCreationFailed({
        error: "FORM_VALUES_INVALID",
        message: firstErrorMessage,
        validation,
      });
  };

  function getCardBrand(cardNumber) {
    const number = cardNumber.replace(/\D/g, "");
    const bin = number.slice(0, 6);

    if (/^4/.test(bin)) return "visa";
    if (/^5[1-5]/.test(bin)) return "mastercard";
    if (/^3[47]/.test(bin)) return "amex";
    if (/^3(?:0[0-5]|[68])/.test(bin)) return "diners";
    if (/^6(?:011|5)/.test(bin)) return "discover";
    if (/^35(2[89]|[3-8][0-9])/.test(bin)) return "jcb";
    if (
      /^(50|60|65|4011|4312|4389|4514|4576|5041|5066|5067|509|6277|6362|6363)/.test(
        bin
      )
    )
      return "elo";
    if (/^606282|3841/.test(bin)) return "hipercard";
    if (/^637095|637568|637599|637609|637612/.test(bin)) return "banescard";

    return "unknown";
  }

  /**
   * Função para criar o token na pagarme
   */
  const createToken = async values => {
    const encryptionKey = process.env.REACT_APP_PAGARME_V5_ENCRYPTION_KEY;

    const cardPayload = {
      type: "card",
      card: {
        number: values.card_number.replace(/\s/g, ""),
        holder_name: values.cardholder_name,
        exp_month: parseInt(values.card_expiration.split("/")[0]),
        exp_year: parseInt(values.card_expiration.split("/")[1]),
        cvv: values.card_cvv,
        label: "Cartão do Cliente",
      },
    };

    try {
      const { data } = await axios.post(
        `https://api.pagar.me/core/v5/tokens?appId=${encryptionKey}`,
        cardPayload
      );

      const tokenData = {
        id: data.id,
        card_hash: data.id,
      };

      const cardTokenizationSuccessProps =
        tracking.checkout.prepareCardTokenizationSuccessForAnalytics(
          values,
          tokenData,
          coupom,
          defaultTrialDays
        );
      trackCheckoutCardTokenizationSuccess(cardTokenizationSuccessProps);

      return data;
    } catch (error) {
      const isCorsError =
        !error.response &&
        (error.message?.includes("CORS") ||
          error.message?.includes("cors") ||
          error.message?.includes("Cross-Origin") ||
          error.message?.includes("cross-origin") ||
          error.code === "ERR_NETWORK" ||
          error.name === "NetworkError");

      if (isCorsError) {
        const checkoutCardTokenizationErrorProps =
          tracking.checkout.prepareCheckoutCardTokenizationErrorForAnalytics(
            values,
            {
              error_type: "cors_error",
              error_message: "Erro de CORS ao gerar token do cartão",
              error_code: "CORS_ERROR",
              cors_details: {
                message: error.message,
                code: error.code,
                name: error.name,
              },
            },
            coupom,
            defaultTrialDays
          );
        trackCheckoutCardTokenizationError(checkoutCardTokenizationErrorProps);
      } else {
        const checkoutCardTokenizationErrorProps =
          tracking.checkout.prepareCheckoutCardTokenizationErrorForAnalytics(
            values,
            {
              error_type: "pagarme_v5_tokenization_error",
              error_message:
                error.response?.data?.message ||
                error.response?.data?.msg ||
                error.message ||
                "Erro ao criar o token do cartão",
              error_code: error.response?.status || "unknown",
            },
            coupom,
            defaultTrialDays
          );
        trackCheckoutCardTokenizationError(checkoutCardTokenizationErrorProps);
      }
      if (enableAddCardSentry && flagsReady) {
        Sentry.withScope(scope => {
          scope.setTag("area", "cadastro");
          scope.setTag("flow", "create_token");
          scope.setTag("provider", "pagarme_v5");
          scope.setTag("trace_id", traceId);
          scope.setExtra("endpoint", "//api.pagar.me/core/v5/tokens");
          scope.setExtra("status", error?.response?.status || "unknown");
          scope.setExtra("code", error?.code);
          scope.setExtra("errors_history", formErrors.slice(-50));
          const sanitized = new Error(
            (error && error.message) || "Card tokenization failed"
          );
          sanitized.name = (error && error.name) || "AxiosError";
          Sentry.captureException(sanitized);
        });
      }
      toast.error(
        "Falha ao prosseguir com o pagamento, por favor, tente novamente."
      );
      setSubmitting(false);
      setDisableButton(false);
      throw new Error("Erro ao criar o token do cartão");
    }
  };

  const createSubscription = async (values, cardData) => {
    try {
      if (isPricingCatalogLoading || isPricingCatalogBlockingError) {
        throw new Error("Catálogo de preços indisponível");
      }

      const { plan, coupomForCheckout } = resolveCheckoutPlanAndCoupom({
        selectedPlan: values.plan,
        coupom,
        pricingCatalogOffers,
      });

      const phonePayload = values.phone
        .replaceAll("(", "")
        .replaceAll(")", "")
        .replaceAll("-", "")
        .replaceAll(" ", "");
      const payload = {
        plan_id: resolveCheckoutPlanId({ coupom: coupomForCheckout, plan }),
        postback_url: null,
        coupom: values.coupom,
        customer: {
          email: values.email,
          name: values.name,
          document_number: values.document_number,
          address: {
            street: values.postal_code,
            street_number: values.address_number,
            neighborhood: values.district,
            zipcode: values.postal_code,
          },
          phone: {
            ddd: phonePayload.slice(0, 2),
            number: phonePayload.slice(2),
          },
        },
        payment_method: "credit_card",
        card_id: cardData.id,
        provider: "pagarme_v5",
        register: buildCheckoutRegisterPayload({ values }),
      };

      return await axios.post(
        `${process.env.REACT_APP_JUSBILL_URL}${getJusbillSubscriptionPath({
          paymentMethod: "credit_card",
        })}`,
        payload,
        { headers: { "x-trace-id": traceId } }
      );
    } catch (error) {
      console.error("Subscription creation failed:", {
        status: error?.response?.status,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          error?.message,
        endpoint: getJusbillSubscriptionPath({ paymentMethod: "credit_card" }),
        trace_id: traceId,
      });
      if (enableAddCardSentry && flagsReady) {
        Sentry.withScope(scope => {
          scope.setTag("area", "cadastro");
          scope.setTag("flow", "create_subscription");
          scope.setTag("provider", "pagarme_v5");
          scope.setTag("trace_id", traceId);
          scope.setExtra(
            "endpoint",
            getJusbillSubscriptionPath({ paymentMethod: "credit_card" })
          );
          scope.setExtra("status", error?.response?.status || "unknown");
          scope.setExtra("code", error?.code);
          scope.setExtra("errors_history", formErrors.slice(-50));
          const sanitized = new Error(
            (error && error.message) || "Subscription creation failed"
          );
          sanitized.name = (error && error.name) || "AxiosError";
          Sentry.captureException(sanitized);
        });
      }
      const paymentFailedProps =
        tracking.checkout.preparePaymentFailedForAnalytics(
          values,
          {
            error_type: "subscription_creation_error",
            error_message:
              error.response?.data?.message ||
              error.response?.data?.msg ||
              error.message ||
              "Erro ao criar a assinatura",
            error_code: error.response?.status || "unknown",
          },
          coupom,
          defaultTrialDays
        );
      trackCheckoutPaymentFailed(paymentFailedProps);

      setSubmitting(false);
      setDisableButton(false);
      throw new Error("Erro ao criar a assinatura");
    }
  };

  const getClientIp = async () => {
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      return data.ip;
    } catch {
      return "unknown";
    }
  };

  const saveCardToBackend = async (token, values) => {
    try {
      const ip = await getClientIp();
      return await axios.post(
        `${process.env.REACT_APP_JUSBILL_URL}/client-cards/pagarme-v5`,
        {
          customerId: values.createdCustomerId,
          email: values.email,
          name: values.name,
          document: values.document_number.replace(/[^0-9]/g, ""),
          phone: values.phone,
          address: {
            zipCode: values.postal_code,
            state: values.state.state,
            address: values.address,
            number: values.address_number,
            neighborhood: values.district,
            city: values.city.name,
            complement: values.address_complement,
          },
          card: {
            token,
            holderName: values.cardholder_name,
            lastDigits: values.card_number.replace(/\s/g, "").slice(-4),
            firstDigits: values.card_number.replace(/\s/g, "").slice(0, 6),
            expirationMonth: values.card_expiration.split("/")[0],
            expirationYear: values.card_expiration.split("/")[1],
            brand: getCardBrand(values.card_number.replace(/\s/g, "")),
          },
          metadata: {
            id: ip ?? 'unknown',
            userAgent: navigator.userAgent,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }
        },
        { headers: { "x-trace-id": traceId } }
      );
    } catch (error) {
      const paymentFailedProps =
        tracking.checkout.preparePaymentFailedForAnalytics(
          values,
          {
            error_type: "card_save_error",
            error_message:
              error.response?.data?.message ||
              error.response?.data?.msg ||
              error.message ||
              "Erro ao registrar o cartão",
            error_code: error.response?.status || "unknown",
          },
          coupom,
          defaultTrialDays
        );
      trackCheckoutPaymentFailed(paymentFailedProps);
      if (enableAddCardSentry && flagsReady) {
        Sentry.withScope(scope => {
          scope.setTag("area", "cadastro");
          scope.setTag("flow", "save_card_to_backend");
          scope.setTag("provider", "pagarme_v5");
          scope.setTag("trace_id", traceId);
          scope.setExtra("endpoint", "/client-cards/pagarme-v5");
          scope.setExtra("status", error?.response?.status || "unknown");
          scope.setExtra("code", error?.code);
          scope.setExtra(
            "card_brand",
            getCardBrand(values.card_number.replace(/\s/g, ""))
          );
          scope.setExtra(
            "card_number",
            `${values.card_number
              .replace(/\s/g, "")
              .slice(0, 6)}***${values.card_number
              .replace(/\s/g, "")
              .slice(-4)}`
          );
          scope.setExtra("errors_history", formErrors.slice(-50));
          const sanitized = new Error(
            (error && error.message) || "Card save failed"
          );
          sanitized.name = (error && error.name) || "AxiosError";
          Sentry.captureException(sanitized);
        });
      }
      setSubmitting(false);
      setDisableButton(false);
      throw new Error("Erro ao registrar o cartão");
    }
  };

  const submitJussub = async (values, paymentMethod = "CREDIT_CARD") => {
    let token = null;
    if (paymentMethod === "CREDIT_CARD") {
      const cardToken = await createToken(values);
      token = cardToken.id;
    }

    const payload = buildJussubCheckoutPayload({
      values,
      token,
      paymentMethod,
      urlParams: buildTrackingUrlParams(props.location.search),
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_JUSSUB_URL}/v1/checkout`,
        payload,
        { headers: { "x-trace-id": traceId } }
      );

      if (!response.data?.subscriptionId) {
        throw new Error("Checkout sem subscriptionId");
      }

      return response.data;
    } catch (error) {
      const paymentFailedProps =
        tracking.checkout.preparePaymentFailedForAnalytics(
          values,
          {
            error_type: "jussub_checkout_error",
            error_message:
              error.response?.data?.message ||
              error.response?.data?.msg ||
              error.message ||
              "Erro ao processar o checkout",
            error_code: error.response?.status || "unknown",
          },
          coupom,
          defaultTrialDays
        );
      trackCheckoutPaymentFailed(paymentFailedProps);
      if (enableAddCardSentry && flagsReady) {
        Sentry.withScope(scope => {
          scope.setTag("area", "cadastro");
          scope.setTag("flow", "jussub_checkout");
          scope.setTag("provider", "jussub");
          scope.setTag("trace_id", traceId);
          scope.setExtra("endpoint", "/v1/checkout");
          scope.setExtra("status", error?.response?.status || "unknown");
          scope.setExtra("code", error?.code);
          scope.setExtra("errors_history", formErrors.slice(-50));
          const sanitized = new Error(
            (error && error.message) || "Jussub checkout failed"
          );
          sanitized.name = (error && error.name) || "AxiosError";
          Sentry.captureException(sanitized);
        });
      }
      setSubmitting(false);
      setDisableButton(false);
      throw error;
    }
  };

  const pullingSubscription = async subscription_id => {
    const MAX_POLLING_TIME = 300 * 1000; // 5 minutos
    const startTime = Date.now();

    const getPollingInterval = elapsedTimeInSeconds => {
      if (elapsedTimeInSeconds <= 60) {
        return 5000; // 5 segundos
      } else if (elapsedTimeInSeconds <= 120) {
        return 10000; // 10 segundos
      } else {
        // restante do tempo (3-5 minutos)
        return 20000; // 20 segundos
      }
    };

    while (Date.now() - startTime < MAX_POLLING_TIME) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_JUSBILL_URL}/subscriptions/${subscription_id}`,
          { headers: { "x-trace-id": traceId } }
        );

        if (response.data.reason) {
          setFailedReason(response.data.reason);

          const paymentFailedProps =
            tracking.checkout.preparePaymentFailedForAnalytics(
              formik.values,
              response.data.reason,
              coupom,
              defaultTrialDays
            );
          trackCheckoutPaymentFailed(paymentFailedProps);
        }

        if (response.data.status === "active") {
          return true;
        }

        if (response.data.status === "failed") {
          setResult("error");
          setSubmitting(false);
          return false;
        }

        const elapsedTimeInSeconds = Math.floor(
          (Date.now() - startTime) / 1000
        );
        const pollingInterval = getPollingInterval(elapsedTimeInSeconds);

        await new Promise(resolve => setTimeout(resolve, pollingInterval));
      } catch (error) {
        console.error("ERROR pullingSubscription", JSON.stringify(error));
        if (enableAddCardSentry && flagsReady) {
          Sentry.withScope(scope => {
            scope.setTag("area", "cadastro");
            scope.setTag("flow", "polling_subscription");
            scope.setTag("provider", "pagarme_v5");
            scope.setTag("trace_id", traceId);
            scope.setExtra("endpoint", `/subscriptions/id`);
            scope.setExtra("status", error?.response?.status || "unknown");
            scope.setExtra("code", error?.code);
            scope.setExtra("errors_history", formErrors.slice(-50));
            const sanitized = new Error(
              (error && error.message) || "Subscription polling failed"
            );
            sanitized.name = (error && error.name) || "AxiosError";
            Sentry.captureException(sanitized);
          });
        }
        const paymentFailedProps =
          tracking.checkout.preparePaymentFailedForAnalytics(
            formik.values,
            {
              error_type: "polling_error",
              error_message: error.message || "Erro no polling da assinatura",
              error_code: error.response?.status || "unknown",
            },
            coupom,
            defaultTrialDays
          );
        trackCheckoutPaymentFailed(paymentFailedProps);

        return false;
      }
    }

    setResult("error");
    setSubmitting(false);
    return false;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: async (values, { setSubmitting }) => {
      Sentry.setExtra("errors_history", formErrors);
      trackSignUpAccountCreationSubmit({
          token: values?.recaptchaToken,
        });

      setDisableButton(true);
      setEnableRecaptchaV2(false);

      var card = {};
      card.card_holder_name = values.cardholder_name;
      card.card_expiration_date =
        values.card_expiration &&
        values.card_expiration.substr(0, 2) +
          "/" +
          values.card_expiration.substr(values.card_expiration.length - 2);
      card.card_number = values.card_number;
      card.card_cvv = values.card_cvv;

      if (
        card.card_holder_name ||
        card.card_expiration_date ||
        card.card_number ||
        card.card_cvv
      ) {
        var cardValidations = pagarme.validate({ card: card });
        if (!cardValidations.card.card_holder_name) {
          trackSignUpAccountCreationFailed({
              error: "PROVIDER_CREDIT_CARD_CHECK_FAILED",
              message: "Verifique o nome impresso no cartão.",
            });
          setSubmitting(false);
          setDisableButton(false);
          toast.error("Verifique o nome impresso no cartão.");
          return false;
        }
        if (!cardValidations.card.card_number) {
          trackSignUpAccountCreationFailed({
              error: "PROVIDER_CREDIT_CARD_CHECK_FAILED",
              message: "Verifique o número do cartão.",
            });
          setSubmitting(false);
          setDisableButton(false);
          toast.error("Verifique o número do cartão.");
          return false;
        }
        if (!cardValidations.card.card_expiration_date) {
          trackSignUpAccountCreationFailed({
              error: "PROVIDER_CREDIT_CARD_CHECK_FAILED",
              message: "Verifique a data de expiração do cartão.",
            });
          setSubmitting(false);
          setDisableButton(false);
          toast.error("Verifique a data de expiração do cartão.");
          return false;
        }
        if (!cardValidations.card.card_cvv) {
          trackSignUpAccountCreationFailed({
              error: "PROVIDER_CREDIT_CARD_CHECK_FAILED",
              message: "Verifique o código de segurança (CVV) do cartão.",
            });
          setSubmitting(false);
          setDisableButton(false);
          toast.error("Verifique o código de segurança (CVV) do cartão.");
          return false;
        }
      }

      let checkoutProvider = null;
      try {
        checkoutProvider = resolveCheckoutProvider({
          isResolved: isJussubResolved,
          isJussubEnabled,
          paymentMethod: "CREDIT_CARD",
        });
      } catch (error) {
        Sentry.captureException(error);
        return false;
      }

      let createCard = {};
      try {
        setSubmitting(true);
        setResult("idle");

        if (checkoutProvider === CHECKOUT_PROVIDER.JUSSUB) {
          const result = await submitJussub(values, "CREDIT_CARD");
          if (result.paymentMethod === "PIX") {
            const pix = result.oneTimePayment;
            const cycleDays = result.plan?.cycleDays;
            setPixPaymentData({
              subscriptionId: result.subscriptionId,
              pix_code: pix?.qrCode,
              charge: {
                amount: result.amount * 100,
                charges: [
                  {
                    last_transaction: {
                      qr_code: pix?.qrCode,
                      qr_code_url: pix?.qrCodeUrl,
                      additional_information: [],
                    },
                  },
                ],
              },
              plan: {
                id: result.plan?.itemId,
                name: result.plan?.name,
                amount: result.plan?.itemAmount,
                type: cycleDays === 365 ? "annually" : "monthly",
              },
            });
            setResult("pix-success");
            setSubmitting(false);
            return;
          }
          setResult("success");
          setSubmitting(false);
          handleCheckoutMarketingData();
          trackSignUpAccountCreated(
            tracking.checkout.prepareSignUpAccountCreatedForAnalytics(values, coupom)
          );
          return;
        }

        const cardToken = await createToken(values);
        createCard = await saveCardToBackend(cardToken.id, values);

        values.card_hash = card.card_number ? createCard.data.token : "";

        values.urlParams = buildTrackingUrlParams(props.location.search);

        if (values.recaptchaVersion === "3") refreshRecaptchaV3Ref.current();

        const subscriptionCreate = await createSubscription(
          values,
          createCard.data
        );
        let pollingResult;
        if (subscriptionCreate.data.status !== "active") {
          pollingResult = await pullingSubscription(subscriptionCreate.data.id);
          if (!pollingResult) {
            setResult("error");
            setSubmitting(false);
            return false;
          }
        }
        if (subscriptionCreate.request.status === 201 || pollingResult) {
          setResult("success");
          setSubmitting(false);
          handleCheckoutMarketingData();

          const checkoutSignUpAttemptProps =
            tracking.checkout.prepareCheckoutSignUpAttemptForAnalytics(
              values,
              coupom,
              defaultTrialDays
            );
          trackCheckoutSignUpAttempt(checkoutSignUpAttemptProps);

          trackSignUpAccountCreated(
            tracking.checkout.prepareSignUpAccountCreatedForAnalytics(values, coupom)
          );
        }
      } catch (error) {
        if (enableAddCardSentry && flagsReady) {
          Sentry.withScope(scope => {
            scope.setTag("area", "cadastro");
            scope.setTag("flow", "checkout");
            scope.setTag("provider", "pagarme_v5");
            scope.setTag("trace_id", traceId);
            scope.setExtra("stage", "onSubmit");
            scope.setExtra("errors_history", formErrors.slice(-50));
            const sanitized = new Error(
              (error && error.message) || "General checkout error"
            );
            scope.setExtra(
              "card_brand",
              getCardBrand(values.card_number.replace(/\s/g, ""))
            );
            scope.setExtra(
              "card_number",
              `${values.card_number
                .replace(/\s/g, "")
                .slice(0, 6)}***${values.card_number
                .replace(/\s/g, "")
                .slice(-4)}`
            );
            sanitized.name = (error && error.name) || "Error";
            Sentry.captureException(sanitized);
          });
        }
        const paymentFailedProps =
          tracking.checkout.preparePaymentFailedForAnalytics(
            values,
            {
              error_type: "general_checkout_error",
              error_message:
                error.message || "Erro durante o processo de checkout",
              error_code: "unknown",
            },
            coupom,
            defaultTrialDays
          );
        trackCheckoutPaymentFailed(paymentFailedProps);

        setResult("error");
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    validationSchema: schemaArray[step],
  });

  useEffect(() => {
    dispatch({ type: "LOAD_STATES" });
    dispatch({ type: "LOAD_PROFESSIONS" });

    const couponInRedirect = props.match.params.redirect?.toUpperCase();

    if (couponInRedirect) {
      formik.setFieldValue("coupom", couponInRedirect);
      setCoupomCode(couponInRedirect);
      requestedPlanIdRef.current = formik.values.plan?.id;
      dispatch({
        type: "LOAD_COUPOM",
        payload: {
          coupom: couponInRedirect,
          tryJussub: true,
          planId: formik.values.plan?.id,
        },
      });

      if (isCaaPrSpecialCoupon(couponInRedirect)) {
        setIsOabPartnershipModalOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O cupom da URL já é validado no mount pela cascata Jussub→Jusbill (LOAD_COUPOM,
  // tryJussub). O /validity do Jussub precifica o cupom PARA o plano selecionado e o
  // seletor segue visível (Step2), então só um cupom que já resolveu como Jussub
  // precisa revalidar a CADA troca de plano pra reprecificar — revalidar um cupom
  // Jusbill a cada troca seria um round-trip sem efeito no preço.
  const selectedPlanId = formik.values.plan?.id;
  const didMountPricingEffectRef = useRef(false);
  // planId que a validação mais recente (mount ou a última troca) usou. Comparado
  // contra selectedPlanId sempre que `coupom` muda — não só quando o plano muda —
  // porque a resposta pode chegar DEPOIS que o usuário já trocou de plano de novo (ex.:
  // a validação do mount só confirma is_jussub:true depois que um plano já foi
  // escolhido); sem essa checagem no `coupom`, essa resposta tardia nunca revalida pro
  // plano atual e o preço mostrado fica preso ao plano errado (ou a nenhum).
  const requestedPlanIdRef = useRef(selectedPlanId);
  useEffect(() => {
    if (!didMountPricingEffectRef.current) {
      // 1ª validação já saiu no efeito de mount, com o planId inicial.
      didMountPricingEffectRef.current = true;
      return;
    }
    const activeCoupon = formik.values.coupom;
    // lastCoupomIsJussubRef (não coupom.is_jussub direto) porque o LOAD_COUPOM abaixo
    // zera coupom pra {} assim que dispara — ler o valor ao vivo aqui perderia a
    // informação numa 2ª troca de plano com a cascata da 1ª ainda em voo.
    if (
      !activeCoupon ||
      lastCoupomIsJussubRef.current !== true ||
      requestedPlanIdRef.current === selectedPlanId
    ) {
      return;
    }
    requestedPlanIdRef.current = selectedPlanId;
    dispatch({
      type: "LOAD_COUPOM",
      payload: {
        coupom: activeCoupon,
        tryJussub: true,
        planId: selectedPlanId,
      },
    });
    // Depende de selectedPlanId E coupom: troca de plano cobre o caso comum (cupom já
    // sabido Jussub, usuário troca de plano); coupom cobre o caso tardio acima.
    // requestedPlanIdRef, atualizado só quando este efeito de fato dispara, evita
    // disparo duplicado quando o próprio LOAD_COUPOM muda `coupom` (loading, depois
    // sucesso) pro MESMO plano — takeLatest (appRedux.loadCoupom) cancela a cascata
    // anterior ainda em voo a cada novo disparo daqui, então uma 2ª troca de plano
    // antes da resposta da 1ª chegar já pede certo pro plano atual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId, coupom]);

  const handleOabPartnershipCouponApplied = coupon => {
    formik.setFieldValue("coupom", coupon);
    setCoupomCode(coupon);
    requestedPlanIdRef.current = formik.values.plan?.id;
    dispatch({
      type: "LOAD_COUPOM",
      payload: {
        coupom: coupon,
        tryJussub: true,
        planId: formik.values.plan?.id,
      },
    });
  };

  // ?plan=<tier> só vale na 1ª reconciliação com o catálogo carregado: depois disso a
  // escolha do usuário no Step2 não pode ser sobrescrita a cada troca de cupom/catálogo.
  const planParamConsumedRef = useRef(false);

  useEffect(() => {
    // O cupom digitado é dono de um tier específico do convênio
    // (ex.: "CAASP_DEGUSTACAO" só desconta a Degustação) — reconcileCatalogPlan prioriza esse tier
    // sobre ?plan= e sobre a seleção anterior, senão o tier de antes da troca de cupom (ex.: ONE) ficava
    // selecionado, mostrando o desconto do tier errado.
    if (!cadastroPlans.length) return;

    const planParam = planParamConsumedRef.current
      ? null
      : new URLSearchParams(location.search).get("plan");
    planParamConsumedRef.current = true;

    const current = formik.values.plan;
    // Travado (ver lockedPlan acima), o card exibido é a única opção possível — não existe
    // escolha manual do usuário pra proteger do planParam já consumido, então lockedPlan
    // prevalece. Senão, remover um cupom que travava outro tier deixava o card exibido (voltando
    // pro plano da URL) divergente do plano realmente enviado no checkout (preso no tier do cupom).
    const reconciled = lockedPlan || reconcileCatalogPlan({
      plans: cadastroPlans,
      selectedPlan: current,
      planParam,
      coupomCode,
    });

    if (reconciled && reconciled !== current) {
      formik.setFieldValue("plan", reconciled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadastroPlans, coupomCode, lockedPlan]);

  const getCoupom = () => {
    setCoupomCode(formik.values.coupom);
    requestedPlanIdRef.current = formik.values.plan?.id;
    dispatch({
      type: "LOAD_COUPOM",
      payload: {
        coupom: formik.values.coupom,
        tryJussub: true,
        planId: formik.values.plan?.id,
      },
    });
  };

  const handleCheckoutMarketingData = () => {
    const isPrdEnv = process.env.NODE_ENV === "production";

    if (isPrdEnv) {
      const plan = formik?.values?.plan;
      let amountConversion = 47.0;
      if (plan && plan.amount) {
        amountConversion = parseFloat(
          plan.amount.replace(".", "").replace(",", ".")
        );
      }

      window.gtag("event", "conversion", {
        send_to: "AW-380889985/PZWiCIe1-soDEIHXz7UB",
        value: amountConversion,
        currency: "BRL",
        transaction_id: "",
      });
    }
  };

  const reset = () => {
    setSubmitting(false);
    setResult("idle");
    setFailedReason({ message: "", details: "" });
    setPixGenerationFailed(false);
    dispatch({ type: "CLEAR_SUBSCRIPTION_ERROR" });
  };

  const onTryAgain = () => {
    const checkoutTryAgainClickedProps =
      tracking.checkout.prepareCheckoutTryAgainClickedForAnalytics(
        formik.values,
        coupom,
        defaultTrialDays
      );
    trackCheckoutTryAgainClicked(checkoutTryAgainClickedProps);
    reset();
  };

  const onAccessPlatform = () => {
    const origin = pixGenerationFailed
      ? "pix-generation-failed-modal-checkout"
      : result === "pix-success"
        ? "pix-success-modal-checkout"
        : "failed-modal-checkout";
    const checkoutAccessPlatformClickedProps =
      tracking.checkout.prepareCheckoutAccessPlatformClickedForAnalytics(
        formik.values,
        coupom,
        defaultTrialDays,
        origin
      );
    trackCheckoutAccessPlatformClicked(checkoutAccessPlatformClickedProps);
    reset();
    history.push(FIRST_ACCESS_ROUTES.BASE, {
      email: formik.values.email,
      fromCheckout: true,
    });
  };

  const createPixSubscription = async values => {
    try {
      if (isPricingCatalogLoading || isPricingCatalogBlockingError) {
        throw new Error("Catálogo de preços indisponível");
      }

      const { plan, coupomForCheckout } = resolveCheckoutPlanAndCoupom({
        selectedPlan: values.plan,
        coupom,
        pricingCatalogOffers,
      });

      const phonePayload = values.phone.replace(/\D/g, "");

      const payload = {
        coupom: values.coupom,
        plan_id: resolveCheckoutPlanId({ coupom: coupomForCheckout, plan }),
        postback_url: null,
        customer: {
          email: values.email,
          name: values.name,
          document_number: values.document_number,
          address: {
            street: values.postal_code,
            street_number: values.address_number,
            neighborhood: values.district,
            zipcode: values.postal_code,
          },
          phone: {
            ddd: phonePayload.slice(0, 2),
            number: phonePayload.slice(2),
          },
        },
        payment_method: "pix",
        provider: "pagarme_v5",
        register: buildCheckoutRegisterPayload({
          values: buildCheckoutValues({
            values,
            urlParams: buildTrackingUrlParams(props.location.search),
          }),
        }),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_JUSBILL_URL}${getJusbillSubscriptionPath({
          paymentMethod: "pix",
        })}`,
        payload,
        { headers: { "x-trace-id": traceId } }
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  const planAmountNumber = Number(
    String(coupom?.amount || formik.values.plan?.amount || "0").replace(
      /[^0-9]/g,
      ""
    )
  );
  const onPix = async () => {
    let checkoutProvider;
    try {
      checkoutProvider = resolveCheckoutProvider({
        isResolved: isJussubResolved,
        isJussubEnabled,
        paymentMethod: "PIX",
      });
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }

    const checkoutPixPaymentClickedProps =
      tracking.checkout.prepareCheckoutPixPaymentClickedForAnalytics(
        formik.values,
        coupom,
        defaultTrialDays
      );
    trackCheckoutPixPaymentClicked(checkoutPixPaymentClickedProps);

    try {
      setSubmitting(true);

      if (checkoutProvider === CHECKOUT_PROVIDER.JUSSUB) {
        const result = await submitJussub(formik.values, "PIX");
        const pix = result.oneTimePayment;
        const cycleDays = result.plan?.cycleDays;
        setPixPaymentData({
          subscriptionId: result.subscriptionId,
          pix_code: pix?.qrCode,
          charge: {
            amount: result.amount * 100,
            charges: [
              {
                last_transaction: {
                  qr_code: pix?.qrCode,
                  qr_code_url: pix?.qrCodeUrl,
                  additional_information: [],
                },
              },
            ],
          },
          plan: {
            id: result.plan?.itemId,
            name: result.plan?.name,
            amount: result.plan?.itemAmount,
            type: cycleDays === 365 ? "annually" : "monthly",
          },
        });
        setResult("pix-success");
        return;
      }

      formik.setFieldValue(
        "urlParams",
        buildTrackingUrlParams(props.location.search)
      );

      const pixResponse = await createPixSubscription(formik.values);
      setPixPaymentData(pixResponse.data);
      setResult("pix-success");
    } catch (error) {
      setResult("error");
      setPixGenerationFailed(true);
      const checkoutPixPaymentFailedProps =
        tracking.checkout.prepareCheckoutPixPaymentFailedForAnalytics(
          formik.values,
          error,
          coupom,
          defaultTrialDays
        );
      trackCheckoutPixPaymentFailed(checkoutPixPaymentFailedProps);
      setFailedReason({
        message: "Erro ao continuar com o Pix",
        details: "Por favor, entre em contato com o suporte.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="register pt-15">
        <div className="container">
          <div className="row mb-10">
            <div className="col-xl-12 d-flex align-items-center">
              <div className="logo flex-grow-1">
                <img
                  src={toAbsoluteUrl("/media/svg/logos/logo-jusfy.svg")}
                  alt="Jusfy"
                />
              </div>
            </div>
          </div>
          {step === 0 && (
            <Step1
              step={step}
              setStep={setStep}
              formik={formik}
              disabledCreditCard={disabledCreditCard}
              isUniqueEmail={uniqueEmail}
              isPricingSettled={!isPricingCatalogLoading}
              oabPartnershipNotice={oabPartnershipNotice}
            />
          )}
          {step === 1 && (
            <CadastroPlanGate
              isPricingResolved={!isPricingCatalogLoading && !isPricingCatalogBlockingError}
              isError={isPricingCatalogBlockingError}
              onRetry={refetchPricingCatalog}
              plans={cadastroPlans}
              selectedPlan={formik.values.plan}
              hasValidCoupon={Boolean(coupom?.is_valid)}
            >
              <Step2
                plans={selectablePlans}
                step={step}
                setStep={setStep}
                formik={formik}
                disabledCreditCard={disabledCreditCard}
                recaptchaV3HandleChange={handleRecaptchaV3Callback}
                recaptchaV2HandleChange={handleRecaptchaV2Callback}
                enableRecaptchaV2={enableRecaptchaV2}
                getCoupom={getCoupom}
                verifyCoupon={verifyCoupon}
                showCupomReferenceInputs={showCupomReferenceInputs}
                setShowCupomReferenceInputs={setShowCupomReferenceInputs}
                searchAddress={code => searchAddressByAddressCode(code)}
                handleSubmitForm={handleSubmitForm}
                isCouponMatchedInCatalog={isCouponMatchedInCatalog}
                disableButton={
                  disableButton ||
                  isPricingCatalogLoading ||
                  isPricingCatalogBlockingError ||
                  isLoadingCoupom
                }
              />
            </CadastroPlanGate>
          )}
        </div>
      </div>
      <ModalProcessingPayment isOpen={submitting && result !== "error"} />
      <ModalPaymentSuccess
        isOpen={result === "success"}
        email={formik.values.email}
        onAccessPlatform={onAccessPlatform}
      />
      <ModalFailedPayment
        isOpen={result === "error"}
        title={failedReason.message}
        subTitle={failedReason.details}
        loading={submitting}
        onPix={onPix}
        onClose={onTryAgain}
        planAmount={planAmountNumber}
        pixGenerationFailed={pixGenerationFailed}
      />
      <ModalCheckoutPixPayment
        isOpen={result === "pix-success"}
        pixPaymentData={pixPaymentData}
        formikValues={formik.values}
        onAccessPlatform={onAccessPlatform}
      />
      <OabPartnershipModal
        isOpen={isOabPartnershipModalOpen}
        urlCoupon={coupomCode}
        onApplyCoupon={handleOabPartnershipCouponApplied}
        onClose={() => setIsOabPartnershipModalOpen(false)}
        onNotice={setOabPartnershipNotice}
      />
    </>
  );
}
